import React, { useEffect, useState } from "react";
import {
  Layout,
  Input,
  Select,
  Tabs,
  message,
  Space,
  TableColumnsType,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import "./index.css";
import DocProcessingBanner from "../../../assets/img/docProcessingBanner.png";
import {
  DeleteOutlined,
  CopyOutlined,
  DownloadOutlined,
  HomeOutlined,
  CodeOutlined,
  FileImageOutlined,
  CustomerServiceOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { get, getBlob, remove } from "../../../utils/apiUtils";
import { getUserRole } from "../../../hooks/getUserRoles";
import Home from "../Home";
import Codes from "../Codes";
import Media from "../Media";
import Highlights from "../Highlights";
import Descriptors from "../Descriptors";
import HomeIcon from "../../../assets/img/homeIcon.png";
import MediaIcon from "../../../assets/img/mediaIcon.png";
import MemoIcon from "../../../assets/img/memoIcon.png";
import CodeIcon from "../../../assets/img/codeIcon.png";
import HighlightIcon from "../../../assets/img/highlightIcon.png";
import DescriptorIcon from "../../../assets/img/descriptorIcon.png";
import AnalyzeIcon from "../../../assets/img/analyzeIcon.png";
import AuditTrailsIcon from "../../../assets/img/auditTrailsIcon.png";
import Analyze from "../Analyze";

// ✅ SHTUAR
import { useAppDispatch } from "../../../store/hooks";
import { clearViewing } from "../Media/media.slice";
import AuditTrails from "../../AuditTrails";
import Memo from "../Memo";

const { Content } = Layout;
const { Search } = Input;
const { Option } = Select;
const { TabPane } = Tabs;

interface DataType {
  key: React.Key;
  filename: string;
  type_name: string;
  date: string;
  actions: JSX.Element;
}

const columns: TableColumnsType<DataType> = [
  {
    title: "History of translations",
    dataIndex: "filename",
  },
  {
    title: "Type",
    dataIndex: "type_name",
  },
  {
    title: "Date",
    dataIndex: "date",
    render: (text) =>
      text === "Invalid date" ? (
        <span style={{ color: "red" }}>{text}</span>
      ) : (
        text
      ),
  },
  {
    title: "",
    dataIndex: "actions",
  },
];

const data: DataType[] = [
  {
    key: "1",
    filename: "Translation 1",
    type_name: "Text",
    date: "05/02/2024",
    actions: (
      <div className="actions">
        <CopyOutlined />
        <DownloadOutlined />
        <DeleteOutlined />
      </div>
    ),
  },
  {
    key: "2",
    filename: "Translation 2",
    type_name: "Json",
    date: "05/02/2024",
    actions: (
      <div className="actions">
        <CopyOutlined />
        <DownloadOutlined />
        <DeleteOutlined />
      </div>
    ),
  },
  {
    key: "3",
    filename: "Translation 3",
    type_name: "File",
    date: "05/02/2024",
    actions: (
      <div className="actions">
        <CopyOutlined />
        <DownloadOutlined />
        <DeleteOutlined />
      </div>
    ),
  },
];

const rowSelection = {
  onChange: (selectedRowKeys: React.Key[], selectedRows: DataType[]) => {},
  getCheckboxProps: (record: DataType) => ({
    disabled: record.filename === "Disabled User",
    name: record.filename,
  }),
};

function DocProcessingList() {
  const { t } = useTranslation("docProcessing");
  const userRole = getUserRole();
  const { id: projectId } = useParams();
  const [activeTab, setActiveTab] = useState("1");

  const [selectionType, setSelectionType] = useState<"checkbox">("checkbox");
  const [searchText, setSearchText] = useState<string>("");
  const [filteredData, setFilteredData] = useState<DataType[]>(data);
  const [typeFilter, setTypeFilter] = useState<string>("Type");
  const [dateFilter, setDateFilter] = useState<Dayjs | string>("");
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [translationList, setTranslationList] = useState<any[]>([]);
  const [translationTypes, setTranslationTypes] = useState<any[]>([]);
  const [jsonName, setJsonName] = useState("");
  const [textName, setTextName] = useState("");
  const [excelName, setExcelName] = useState("");
  const [loading, setLoading] = useState(false);
  const [mediaRevision, setMediaRevision] = useState(0);
  const [highlightsRevision, setHighlightsRevision] = useState(0);
  const navigate = useNavigate();

  const dispatch = useAppDispatch(); // ✅

  const onMediaChanged = () => setMediaRevision((r) => r + 1);
  const onHighlightChanged = () => setHighlightsRevision((r) => r + 1);

  useEffect(() => {
    if (activeTab === "1") setMediaRevision((r) => r + 1); // Home
    if (activeTab === "4") setHighlightsRevision((r) => r + 1); // Highlights
  }, [activeTab]);

  // ✅ sa herë që del prej Media tab (key "3"), pastro viewingHtml
  useEffect(() => {
    if (activeTab !== "3") {
      dispatch(clearViewing());
    }
  }, [activeTab, dispatch]);

  const filterData = (): void => {
    const lowercasedSearchText = searchText.toLowerCase();
    const filtered = translationList.filter((item: any) => {
      const searchTextMatch = item.filename
        .toLowerCase()
        .includes(lowercasedSearchText);
      const typeMatch = typeFilter === "Type" || item.type_name === typeFilter;
      const dateMatch =
        !dateFilter || dayjs(item.date).isSame(dayjs(dateFilter), "day");
      return searchTextMatch && typeMatch && dateMatch;
    });
    setFilteredData(filtered);
  };

  const copyTranslation = async (id: React.Key) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await getBlob<Blob>(
        `copy_translation/${id}?project_id=${projectId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: "blob",
        },
      );

      if (response.type === "success") {
        const text = await response.data.text();
        if (response.headers["content-type"]?.includes("application/json")) {
          const json = JSON.parse(text);
          if (json.content && typeof json.content === "object") {
            await navigator.clipboard.writeText(JSON.stringify(json.content));
          } else {
            await navigator.clipboard.writeText(json.content);
          }
        } else {
          await navigator.clipboard.writeText(text);
        }
        message.success(t("docProcessingList.copySuccess"));
      } else {
        console.error("Error copying the translation:", response);
        message.error(t("docProcessingList.copyError"));
      }
    } catch (error) {
      console.error("Error copying the translation:", error);
      message.error(t("docProcessingList.copyError"));
    } finally {
      setLoading(false);
    }
  };

  const downloadTranslation = async (
    id: React.Key,
    filename: any,
    type: any,
  ) => {
    setLoading(true);
    try {
      const response = await getBlob<Blob>(
        `translations/download/${id}?project_id=${projectId}`,
        {
          responseType: "blob",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      if (response.type === "success") {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        const fileExtension =
          type === "excel" ? ".xlsx" : type === "json" ? ".json" : ".txt";
        link.href = url;
        link.setAttribute("download", `${filename}${fileExtension}`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        message.success(t("docProcessingList.downloadSuccess"));
      } else {
        throw new Error(
          "Failed to download file, server responded with error.",
        );
      }
    } catch (error) {
      console.error("Failed to download translation:", error);
      message.error(t("docProcessingList.downloadError"));
    } finally {
      setLoading(false);
    }
  };

  const deleteTranslation = async (id: React.Key) => {
    if (!window.confirm(t("docProcessingList.deleteConfirm"))) {
      return;
    }
    setLoading(true);
    try {
      const response = await remove(
        `translations/${id}?project_id=${projectId}`,
        {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      if (response.type === "success") {
        message.success(t("docProcessingList.deleteSuccess"));
        await fetchTranslations();
      } else {
        throw new Error("Server responded with an error during deletion.");
      }
    } catch (error) {
      console.error("Failed to delete translation:", error);
      message.error(t("docProcessingList.deleteError", { error }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTranslations();
  }, [projectId]);

  const isValidDate = (dateStr: any, format = "MM/DD/YYYY") => {
    return dayjs(dateStr, format).isValid();
  };

  const fetchTranslations = async () => {
    setLoading(true);
    const token = localStorage.getItem("token");
    let response = await get<any>(
      `users/translations?project_id=${projectId}`,
      {},
    );
    if (response.type === "success") {
      const fetchedTranslations = response.data.map((item: any) => {
        const isValid = isValidDate(item.date);
        if (!isValid) {
          console.error(`Invalid date format for item with id ${item.id}`);
        }
        return {
          ...item,
          key: item.id,
          date: isValid
            ? dayjs(item.date).format("MM/DD/YYYY")
            : "Invalid date",
          actions: (
            <Space size="middle" className="actions">
              {item.type_name !== "excel" && (
                <CopyOutlined onClick={() => copyTranslation(item.id)} />
              )}
              <DownloadOutlined
                onClick={() =>
                  downloadTranslation(item.id, item.filename, item.type_name)
                }
              />
              <DeleteOutlined onClick={() => deleteTranslation(item.id)} />
            </Space>
          ),
        };
      });
      setTranslationList(fetchedTranslations);
      setFilteredData(fetchedTranslations);
      setLoading(false);
    }
  };

  useEffect(() => {
    filterData();
  }, [searchText, typeFilter, dateFilter, translationList]);

  const onSearch = (value: string) => {
    setSearchText(value);
    filterData();
  };

  const handleRedirect = () => {
    navigate(`/add-ai-translation/${projectId}`);
  };

  return (
    <div style={{ marginLeft: 0 }} className="doc-processing-layout">
      <Content
        className="doc-processing-content"
        style={{
          padding: "0 24",
          margin: 0,
          minHeight: 280,
        }}
      >
        <div className="header-image-for-ai-translation">
          <img
            src={DocProcessingBanner}
            alt="AI Tool Header"
            style={{ width: "100%" }}
          />
        </div>

        <Tabs
          defaultActiveKey="1"
          activeKey={activeTab}
          onChange={setActiveTab}
          centered
          className="new-tool-content custom-tabs"
          destroyInactiveTabPane // ✅ reseton Media / Highlights / Analyze kur ndrron tab
        >
          <TabPane
            tab={
              <span className="custom-tab">
                <img src={HomeIcon} /> {t("docProcessingList.tabs.home")}
              </span>
            }
            key="1"
          >
            <div className="custom-tab-content">
              <Home />
            </div>
          </TabPane>

          <TabPane
            tab={
              <span className="custom-tab">
                <img src={CodeIcon} />
                {t("docProcessingList.tabs.codes")}
              </span>
            }
            key="2"
          >
            <div className="custom-tab-content">
              <Codes />
            </div>
          </TabPane>

          <TabPane
            tab={
              <span className="custom-tab">
                <img src={MediaIcon} />
                {t("docProcessingList.tabs.media")}
              </span>
            }
            key="3"
          >
            <div className="custom-tab-content">
              <Media />
            </div>
          </TabPane>
          <TabPane
            tab={
              <span className="custom-tab">
                <img src={MemoIcon} />
                {t("docProcessingList.tabs.memo")}
              </span>
            }
            key="4"
          >
            <div className="custom-tab-content">
              <Memo />
            </div>
          </TabPane>

          <TabPane
            tab={
              <span className="custom-tab">
                <img src={HighlightIcon} />
                {t("docProcessingList.tabs.highlights")}
              </span>
            }
            key="5"
          >
            <div className="custom-tab-content">
              <Highlights />
            </div>
          </TabPane>

          <TabPane
            tab={
              <span className="custom-tab">
                <img src={DescriptorIcon} /> {t("docProcessingList.tabs.descriptors")}
              </span>
            }
            key="6"
          >
            <div className="custom-tab-content">
              <Descriptors />
            </div>
          </TabPane>

          <TabPane
            tab={
              <span className="custom-tab">
                <img src={AnalyzeIcon} /> {t("docProcessingList.tabs.analyze")}
              </span>
            }
            key="7"
          >
            <div className="custom-tab-content">
              <Analyze />
            </div>
          </TabPane>
          {/* <TabPane
            tab={
              <span className="custom-tab">
                <img src={AuditTrailsIcon} /> Audit trails
              </span>
            }
            key="8"
          >
            <div className="custom-tab-content">
              <AuditTrails />
            </div>
          </TabPane> */}
        </Tabs>
      </Content>
    </div>
  );
}

export default DocProcessingList;
